import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowLeft, Users, Plus, RefreshCw, Banknote, CreditCard, Calendar, Ticket, Mail } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { useAuth } from "../../context/AuthContext";
import AccountantTickets from "./AccountantTickets";
import SellCoupon from "./SellCoupon";
import WalkinSale from "./WalkinSale";
import CouponLookup from "./CouponLookup";
import CouponReprint from "./CouponReprint";
import { usePosCharge, PosFeeNote, posFee } from "./usePosCharge";
import DeskHeader from "./DeskHeader";
import DeskTiles from "./DeskTiles";
import DeskBottomBar from "./DeskBottomBar";
import ScanBar from "./ScanBar";
import EventSummary from "./EventSummary";
import RecentActivity from "./RecentActivity";
import DeskSale from "./DeskSale";
import ScanResultOverlay from "./ScanResultOverlay";

export default function EventDetailDashboard({ eventId, onBack }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("summary");
  const [searchQuery, setSearchQuery] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [deskTab, setDeskTab] = useState("desk");
  const [refreshKey, setRefreshKey] = useState(0);
  const [ticketScan, setTicketScan] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupPaymentType, setTopupPaymentType] = useState("cash");
  const [topupLoading, setTopupLoading] = useState(false);
  const [refundMode, setRefundMode] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  // Coupon lookup (mirrors MerchantDashboard.js — one box, one API call chain).
  const [foundCoupon, setFoundCoupon] = useState(null);
  const [couponFromLookup, setCouponFromLookup] = useState(false);
  const [couponSearch, setCouponSearch] = useState("");
  const posRule = usePosCharge();
  const [couponRefunding, setCouponRefunding] = useState(false);
  const { authAxios } = useAuth();

  const QUICK_AMOUNTS = [10, 20, 50, 100];

  useEffect(() => { fetchData(); }, [eventId]);

  const fetchData = async () => {
    setRefreshKey(k => k + 1);
    try { const anaRes = await authAxios.get(`/analytics/accountant/event/${eventId}`); setData(anaRes.data); }
    catch (e) { toast.error("Failed to load data"); }
  };

  const searchWallet = async (walletId, target = "walletSearch") => {
    if (!walletId) return toast.error("Enter wallet or coupon code");
    // Wallet first (member/guest QR codes are prefixed MM-/MG-).
    try {
      const res = await authAxios.get(`/wallet/balance/${walletId}`);
      setFoundCustomer(res.data); setCustomerTransactions(res.data.wallet?.expiring_details || []); setView(target);
      return;
    } catch (walletErr) {
      const status = walletErr.response?.status;
      if (status !== 404) {
        toast.error(walletErr.response?.data?.detail || "Wallet lookup failed");
        return;
      }
    }
    // Coupon fallback — reuses the accountant-facing details endpoint so we
    // get transactions + balance in one call.
    try {
      const res = await authAxios.get(`/coupons/${walletId.toUpperCase()}/details`);
      setFoundCoupon(res.data); setCouponFromLookup(false); setView("couponDetail");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No wallet or coupon matches that code");
    }
  };

  const openCoupon = async (c) => {
    try {
      const res = await authAxios.get(`/coupons/${c.code}/details`);
      setFoundCoupon(res.data); setCouponFromLookup(true); setView("couponDetail");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open coupon"); }
  };

  const handleCouponRefund = async () => {
    if (!foundCoupon) return;
    setCouponRefunding(true);
    try {
      const res = await authAxios.post("/coupons/refund", { coupon_code: foundCoupon.code });
      toast.success(res.data.message || "Coupon refunded");
      const detail = await authAxios.get(`/coupons/${foundCoupon.code}/details`);
      setFoundCoupon(detail.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setCouponRefunding(false);
    }
  };

  const validateTicket = async (qr) => {
    try { const r = await authAxios.post("/ticketing/scan/validate", { qr_code: qr }); setTicketScan(r.data); }
    catch (e) { setTicketScan({ valid: false, message: e.response?.data?.detail || "Scan failed" }); }
  };

  const markAttendance = async () => {
    if (!ticketScan?.ticket_id) return;
    setConfirming(true);
    try {
      const res = await authAxios.post("/ticketing/scan/confirm", { ticket_id: ticketScan.ticket_id });
      const credit = res.data?.credit;
      const who = ticketScan.attendee_name || ticketScan.buyer_name || "guest";
      toast.success(credit?.issued ? `Attendance marked for ${who} · \u20ac${credit.amount.toFixed(2)} credited to ${credit.destination === "wallet" ? "wallet" : `coupon ${credit.coupon_code}`}` : `Attendance marked for ${who}`);
      setTicketScan(null); setScanOpen(true); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to mark attendance"); }
    finally { setConfirming(false); }
  };

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const resolveScan = (raw) => {
    const text = raw.trim();
    if (text.includes("@")) { setCouponSearch(text); setView("couponLookup"); return; }
    if (/^MW[A-Z]*:/i.test(text) || UUID_RE.test(text) || text.length > 20) return validateTicket(text);
    searchWallet(text.toUpperCase());
  };

  const handleTopup = async () => {
    if (!topupAmount || parseFloat(topupAmount) <= 0) return toast.error("Enter valid amount");
    setTopupLoading(true);
    try {
      const res = await authAxios.post("/wallet/topup", { customer_id: foundCustomer.customer.customer_id, event_id: eventId, amount: parseFloat(topupAmount), payment_type: topupPaymentType });
      toast.success(res.data.message);
      const refreshRes = await authAxios.get(`/wallet/balance/${foundCustomer.customer.customer_id}`);
      setFoundCustomer(refreshRes.data); setTopupAmount(""); setView("walletSearch"); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Top-up failed"); }
    finally { setTopupLoading(false); }
  };

  const handleRefund = async () => {
    const amount = parseFloat(refundAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setRefundLoading(true);
    try {
      const res = await authAxios.post("/wallet/refund", { customer_id: foundCustomer.customer.customer_id, amount });
      toast.success(res.data.message);
      const refreshRes = await authAxios.get(`/wallet/balance/${foundCustomer.customer.customer_id}`);
      setFoundCustomer(refreshRes.data); setRefundAmount(""); setRefundMode(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setRefundLoading(false); }
  };

  if (!data) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  // Account search result view
  if (view === "walletSearch" && foundCustomer) {
    return (
      <div data-testid="wallet-search-result">
        <button onClick={() => { setFoundCustomer(null); setView("summary"); setSearchQuery(""); }} className="flex items-center gap-2 text-slate-600 mb-6"><ArrowLeft className="w-5 h-5" /> Back to Event</button>
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-[#1e3a5f] flex items-center justify-center"><Users className="w-8 h-8 text-white" /></div>
            <div className="flex-1"><h2 className="text-xl font-bold text-[#1e3a5f]">{foundCustomer.customer.name}</h2><p className="text-slate-500 font-mono">{foundCustomer.customer.customer_id}</p></div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-3 gap-4 text-center">
            <div><p className="text-slate-500 text-xs">Total Balance</p><p className="text-2xl font-bold text-[#1e3a5f]">{"\u20ac"}{foundCustomer.wallet.total_balance.toFixed(2)}</p></div>
            <div><p className="text-slate-500 text-xs">No Expiry</p><p className="text-xl font-bold text-green-600">{"\u20ac"}{foundCustomer.wallet.perpetual_balance.toFixed(2)}</p></div>
            <div><p className="text-slate-500 text-xs">Non-Refundable</p><p className="text-xl font-bold text-amber-600">{"\u20ac"}{foundCustomer.wallet.expiring_balance.toFixed(2)}</p></div>
          </div>
        </div>
        {data.event.status === "active" && data.event.settlement_status !== "settled" && (
          <button onClick={() => setView("walletTopup")} className="w-full bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f] rounded-2xl p-4 flex items-center justify-center gap-3 text-white font-bold mb-3" data-testid="topup-customer-btn"><Plus className="w-5 h-5" /> Top-up This Account</button>
        )}
        {foundCustomer.wallet.perpetual_balance > 0 && (
          !refundMode ? (
            <button onClick={() => { setRefundMode(true); setRefundAmount(foundCustomer.wallet.perpetual_balance.toFixed(2)); }} className="w-full bg-red-500 rounded-2xl p-4 flex items-center justify-center gap-3 text-white font-bold mb-6" data-testid="acc-start-refund-btn"><RefreshCw className="w-5 h-5" /> Refund Balance</button>
          ) : (
            <div className="bg-red-50 rounded-2xl p-4 mb-6" data-testid="acc-refund-form">
              <h3 className="font-bold text-red-700 mb-2">Refund Balance</h3>
              <p className="text-sm text-red-600 mb-3">Refundable (excl. booking): <span className="font-bold">{"\u20ac"}{foundCustomer.wallet.perpetual_balance.toFixed(2)}</span></p>
              <input type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} placeholder="Refund amount" className="w-full mb-3 p-3 border border-red-200 rounded-xl text-lg font-bold text-center" data-testid="acc-refund-amount-input" />
              <div className="flex gap-2">
                <button onClick={() => { setRefundMode(false); setRefundAmount(""); }} className="flex-1 py-3 rounded-xl border font-bold text-slate-600">Cancel</button>
                <button onClick={handleRefund} disabled={refundLoading || !refundAmount || parseFloat(refundAmount) <= 0} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold disabled:opacity-50" data-testid="acc-confirm-refund-btn">{refundLoading ? "Processing..." : `Refund \u20ac${refundAmount || "0"}`}</button>
              </div>
            </div>
          )
        )}
        {foundCustomer.transactions && foundCustomer.transactions.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-6"><h3 className="font-bold mb-3">Recent Transactions</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {foundCustomer.transactions.map((t, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b last:border-0">
                  <div><p className={`text-sm font-medium ${t.type === "topup" ? "text-blue-600" : (typeof t.type === "string" && t.type.includes("refund")) ? "text-green-600" : "text-red-600"}`}>{t.type === "topup" ? "Top-up" : (typeof t.type === "string" && t.type.includes("refund")) ? "Refund" : "Spent"}{t.event_name && t.event_name !== "Direct" ? ` - ${t.event_name}` : ""}</p>{t.merchant_name && <p className="text-xs text-slate-500">{t.merchant_name}</p>}<p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</p></div>
                  <p className={`font-bold ${t.type === "topup" || (typeof t.type === "string" && t.type.includes("refund")) ? "text-green-600" : "text-red-600"}`}>{t.type === "topup" || (typeof t.type === "string" && t.type.includes("refund")) ? "+" : "-"}{"\u20ac"}{t.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        <button onClick={() => { setFoundCustomer(null); setSearchQuery(""); }} className="w-full py-3 rounded-full border-2 border-slate-300 text-slate-600 font-bold">Search Another Account</button>
      </div>
    );
  }

  // Top-up view
  if (view === "walletTopup" && foundCustomer) {
    return (
      <div data-testid="wallet-topup-form">
        <button onClick={() => setView("walletSearch")} className="flex items-center gap-2 text-slate-600 mb-6"><ArrowLeft className="w-5 h-5" /> Back to Customer</button>
        <div className="bg-[#1e3a5f] rounded-2xl p-4 mb-4 text-white"><p className="text-white/70 text-xs">Event</p><p className="font-bold">{data.event.name}</p></div>
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#1e3a5f] flex items-center justify-center"><Users className="w-6 h-6 text-white" /></div>
          <div className="flex-1"><p className="font-bold text-[#1e3a5f]">{foundCustomer.customer.name}</p><p className="text-slate-500 text-sm font-mono">{foundCustomer.customer.customer_id}</p></div>
          <div className="text-right"><p className="text-slate-500 text-xs">Balance</p><p className="text-xl font-bold text-green-600">{"\u20ac"}{foundCustomer.wallet.total_balance.toFixed(2)}</p></div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <label className="text-sm text-slate-500 mb-3 block">Quick Top-up</label>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {QUICK_AMOUNTS.map(amt => (<button key={amt} onClick={() => setTopupAmount(amt.toString())} className={`py-3 rounded-xl font-bold text-lg transition-colors ${topupAmount === amt.toString() ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid={`quick-amount-${amt}`}>{"\u20ac"}{amt}</button>))}
          </div>
          <label className="text-sm text-slate-500 mb-2 block">Or enter custom amount:</label>
          <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">{"\u20ac"}</span><input type="number" value={topupAmount} onChange={e => setTopupAmount(e.target.value)} placeholder="0.00" className="w-full p-3 pl-8 rounded-xl border text-xl font-bold" min="0" step="0.01" data-testid="topup-amount-input" /></div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <label className="text-sm text-slate-500 mb-3 block">Payment Type</label>
          <div className="grid grid-cols-3 gap-2">
            {["cash", "card", "booking"].map(type => (<button key={type} onClick={() => setTopupPaymentType(type)} className={`py-3 rounded-xl font-bold capitalize flex items-center justify-center gap-2 transition-colors ${topupPaymentType === type ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid={`payment-${type}`}>{type === "cash" && <Banknote className="w-4 h-4" />}{type === "card" && <CreditCard className="w-4 h-4" />}{type === "booking" && <Calendar className="w-4 h-4" />}{type}</button>))}
          </div>
          {topupPaymentType === "card" && <PosFeeNote rule={posRule} amount={parseFloat(topupAmount) || 0} testid="topup-pos-fee" />}
          {topupPaymentType === "booking" && <p className="text-amber-500 text-xs mt-2">Note: Booking balance expires at the end of the event and is non-refundable.</p>}
        </div>
        <button onClick={handleTopup} disabled={topupLoading || !topupAmount || parseFloat(topupAmount) <= 0} className="w-full py-4 rounded-full bg-green-500 text-white font-bold disabled:opacity-50" data-testid="confirm-topup-btn">{topupLoading ? "Processing..." : `Top-up \u20ac${topupAmount || "0"}${topupPaymentType === "card" && posFee(posRule, parseFloat(topupAmount) || 0) > 0 ? ` · collect \u20ac${((parseFloat(topupAmount) || 0) + posFee(posRule, parseFloat(topupAmount) || 0)).toFixed(2)}` : ""}`}</button>
      </div>
    );
  }

  // Ticket management view
  if (view === "tickets") {
    return <AccountantTickets eventId={eventId} onBack={() => { setView("summary"); fetchData(); }} />;
  }

  if (view === "walkin") {
    return <WalkinSale eventId={eventId} onBack={() => { setView("summary"); fetchData(); }} />;
  }

  // Sell Coupon view — accountant flow, event context comes from this dashboard.
  if (view === "sellCoupon") {
    return (
      <SellCoupon
        eventId={eventId}
        eventName={data.event.name}
        eventStart={data.event.start_date}
        eventEnd={data.event.end_date}
        onBack={() => { setView("summary"); fetchData(); }}
      />
    );
  }

  // Coupon drill-in — shown when the search box surfaced a coupon (mirrors the
  // wallet drill-in above so the accountant sees the same info in one place).
  if (view === "couponLookup") {
    return <CouponLookup eventId={eventId} initialQuery={couponSearch} onBack={() => { setCouponSearch(""); setView("summary"); }} onOpen={openCoupon} />;
  }

  if (view === "activity") {
    const openActivity = (it) => {
      if (it.kind === "walkin") setView("walkin");
      else if (it.kind === "coupon") openCoupon({ code: it.ref });
      else if (it.kind === "desk") { if (it.who) searchWallet(it.who); }
      else searchWallet(it.ref);
    };
    return <RecentActivity eventId={eventId} onBack={() => setView("summary")} onOpen={openActivity} />;
  }

  if (view === "deskSale") {
    return <DeskSale eventId={eventId} onBack={() => { setView("summary"); fetchData(); }} />;
  }

  if (view === "topupSearch") {
    return (
      <div data-testid="topup-search">
        <button onClick={() => setView("summary")} className="flex items-center gap-2 text-slate-600 mb-4"><ArrowLeft className="w-5 h-5" /> Back to Event</button>
        <h2 className="font-bold text-lg text-[#1e3a5f] mb-1">Wallet Top-up</h2>
        <p className="text-sm text-slate-500 mb-3">Scan the member/guest wallet QR or type the MM-/MG- wallet ID.</p>
        <ScanBar onResolve={(t) => searchWallet(t.toUpperCase(), "walletTopup")} scanOpen={scanOpen} setScanOpen={setScanOpen} />
      </div>
    );
  }

  if (view === "couponDetail" && foundCoupon) {
    const isDirect = foundCoupon.source === "direct_purchase";
    const canRefund = isDirect && foundCoupon.balance > 0;
    return (
      <div data-testid="coupon-detail">
        <button onClick={() => { setFoundCoupon(null); setView(couponFromLookup ? "couponLookup" : "summary"); setSearchQuery(""); }} className="flex items-center gap-2 text-slate-600 mb-6" data-testid="coupon-detail-back-btn"><ArrowLeft className="w-5 h-5" /> {couponFromLookup ? "Back to Coupons" : "Back to Event"}</button>
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center"><Ticket className="w-8 h-8 text-amber-600" /></div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-amber-600 font-bold mb-1">Coupon</p>
              <p className="font-mono text-xl font-bold text-[#1e3a5f]">{foundCoupon.code}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${foundCoupon.status === "unused" ? "bg-green-100 text-green-600" : (foundCoupon.status === "refunded" || foundCoupon.status === "withdrawn") ? "bg-red-100 text-red-600" : foundCoupon.status === "depleted" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-600"}`}>{(foundCoupon.status || "").toUpperCase()}</span>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-2 gap-4 text-center mb-3">
            <div><p className="text-slate-500 text-xs">Face Value</p><p className="text-xl font-bold text-[#1e3a5f]">{"\u20ac"}{foundCoupon.amount.toFixed(2)}</p></div>
            <div><p className="text-slate-500 text-xs">Balance</p><p className="text-xl font-bold text-green-600">{"\u20ac"}{foundCoupon.balance.toFixed(2)}</p></div>
          </div>
          <div className="space-y-1 text-sm">
            {foundCoupon.issued_to_email && (<p className="text-slate-500 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {foundCoupon.issued_to_email}</p>)}
            {foundCoupon.valid_dates && foundCoupon.valid_dates.length > 0 && (<p className="text-slate-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Valid: {foundCoupon.valid_dates.join(", ")}</p>)}
            <p className="text-slate-500">Payment: <span className="capitalize">{foundCoupon.payment_type}</span></p>
            {foundCoupon.tax_amount > 0 && <p className="text-slate-500">Charges collected: <strong>{"\u20ac"}{foundCoupon.tax_amount.toFixed(2)}</strong></p>}
            <p className="text-slate-500">Issued: {new Date(foundCoupon.created_at).toLocaleString()}</p>
            {!isDirect && <p className="text-amber-600 text-xs mt-2">Event-credit coupon {"\u2014"} non-refundable.</p>}
          </div>
        </div>
        <CouponReprint key={foundCoupon.code} coupon={foundCoupon} onEmailed={() => authAxios.get(`/coupons/${foundCoupon.code}/details`).then(r => setFoundCoupon(r.data)).catch(() => {})} />
        {canRefund && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="w-full py-3 rounded-full bg-red-500 text-white font-bold flex items-center justify-center gap-2 mb-4 disabled:opacity-50" disabled={couponRefunding} data-testid="coupon-refund-btn">
                <RefreshCw className="w-4 h-4" /> Refund {"\u20ac"}{foundCoupon.balance.toFixed(2)}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Refund this coupon?</AlertDialogTitle>
                <AlertDialogDescription>
                  Refunding will set the balance to €0 and (if we have their email on file) notify the buyer. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleCouponRefund} className="bg-red-500 hover:bg-red-600" data-testid="coupon-confirm-refund-btn">
                  Refund {"\u20ac"}{foundCoupon.balance.toFixed(2)}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="font-bold mb-2 text-[#1e3a5f]">Transactions</h3>
          {(!foundCoupon.transactions || foundCoupon.transactions.length === 0) ? (
            <p className="text-slate-400 text-sm text-center py-4">No transactions yet</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {foundCoupon.transactions.map((t) => (
                <div key={t.id} className="flex justify-between items-center py-2 border-b last:border-0">
                  <div>
                    <p className={`text-sm font-medium ${(typeof t.type === "string" && t.type.includes("refund")) ? "text-red-600" : "text-green-600"}`}>{(typeof t.type === "string" && t.type.includes("refund")) ? "Refund" : "Sale"}{t.merchant_name ? ` \u2014 ${t.merchant_name}` : ""}</p>
                    <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</p>
                  </div>
                  <p className={`font-bold ${(typeof t.type === "string" && t.type.includes("refund")) ? "text-red-600" : "text-green-600"}`}>{(typeof t.type === "string" && t.type.includes("refund")) ? "+" : "-"}{"\u20ac"}{t.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const go = (v) => { if (v === "topupSearch") { setView("topupSearch"); return; } setView(v); };
  const onNav = (id) => {
    if (id === "scan") { setDeskTab("desk"); setScanOpen(true); return; }
    if (id === "summary") { setDeskTab("summary"); return; }
    setDeskTab("desk");
    setTimeout(() => document.getElementById(id === "sell" ? "desk-sell" : "desk-lookup")?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  // Main event detail view
  return (
    <div className="pb-20 md:pb-0" data-testid="event-detail">
      <DeskHeader event={data.event} eventId={eventId} refreshKey={refreshKey} onBack={onBack} />
      <div className="hidden md:flex gap-1 mb-4 bg-slate-100 rounded-full p-1 w-fit" data-testid="desk-tabs">
        {[["desk", "Desk"], ["summary", "Summary"]].map(([id, label]) => (
          <button key={id} onClick={() => setDeskTab(id)} data-testid={`desk-tab-${id}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${deskTab === id ? "bg-white text-[#1e3a5f] shadow-sm" : "text-slate-500"}`}>{label}</button>
        ))}
      </div>
      {deskTab === "desk" ? (
        <>
          <ScanBar onResolve={resolveScan} scanOpen={scanOpen} setScanOpen={setScanOpen} />
          <DeskTiles event={data.event} posRule={posRule} go={go} />
        </>
      ) : (
        <EventSummary data={data} eventId={eventId} onChanged={fetchData} />
      )}
      {ticketScan && (
        <ScanResultOverlay result={ticketScan} confirming={confirming} onMark={markAttendance}
          onClose={() => setTicketScan(null)} onScanAnother={() => { setTicketScan(null); setScanOpen(true); }} />
      )}
      <DeskBottomBar active={deskTab === "summary" ? "summary" : null} onSelect={onNav}
        canSell={data.event.status === "active" && data.event.settlement_status !== "settled"} />
    </div>
  );
}
